<template>
    <div>
        <kgr-lightbox
            v-model="showSelectedActionMember"
            :headline="liveUpdating ? $t('physicalAccess.log.newestActionLogMember') : $t('physicalAccess.log.shortProfile')"
            animated
        >
            <kgr-loader v-if="!(selectedAction && selectedAction.shortProfile)" />
            <kgr-member-part-basic
                v-else
                :member-short-profile="selectedAction.shortProfile"
                class="kgr-row kgr-row--bigMargin"
            />
        </kgr-lightbox>

        <div class="kgr-row">
            {{ $t('physicalAccess.log.intro') }}
        </div>

        <div class="kgr-grid kgr-grid--full kgr-grid--center kgr-grid--medium-1of6 kgr-grid--small-1of3 kgr-grid--tiny-full kgr-grid--gutters">
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup__label">{{ $t('Startdatum') }}</label>
                <kgr-date-picker
                    v-model="formattedStartDate"
                    :disabled="liveUpdating"
                    @post-input="preLoad"
                />
            </div>
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup__label">{{ $t('Enddatum') }}</label>
                <kgr-date-picker
                    v-model="formattedEndDate"
                    :disabled="liveUpdating"
                    @post-input="preLoad"
                />
            </div>
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup__label">{{ $t('physicalAccess.log.time') }}</label>
                <kgr-time-range-picker
                    :start="formattedStartTime"
                    :end="formattedEndTime"
                    :disabled="liveUpdating"
                    @change="onChangeTimeRange($event)"
                />
            </div>
            <div class="kgr-grid__cell">
                <kgr-radio
                    v-model="selectedType"
                    :options="typeOptions"
                    :disabled="liveUpdating"
                    inline
                    @post-input="preLoad"
                />
            </div>
            <div
                class="kgr-grid__cell"
            >
                <div v-show="selectedType">
                    <label class="kgr-inputGroup__label">{{ $t('Terminals') }}</label>
                    <kgr-multi-select
                        v-model="selectedTerminals"
                        :options="filteredTerminals"
                        :disabled="liveUpdating"
                        :inline="false"
                        @post-input="preLoad"
                    />
                </div>
            </div>
            <div class="kgr-grid__cell kgr-grid__cell--full">
                <kgr-checkbox
                    :checked="liveUpdating"
                    :readonly="disableLiveUpdating"
                    :label="$t('physicalAccess.log.liveUpdating')"
                    inline
                    @change="liveUpdate($event)"
                />
            </div>
        </div>

        <div>
            <kgr-loader v-if="loading" />
            <div v-else>
                <kgr-empty-data
                    v-if="accessMediumIdentifications.length === 0"
                    :value="$t('physicalAccess.log.noData')"
                />
                <table
                    v-else
                    class="kgr-table kgr-table--striped kgr-table--block table-groups"
                >
                    <thead>
                        <tr class="kgr-table__row">
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('Datum') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('Uhrzeit') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('physicalAccess.log.terminalIdentifier') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('physicalAccess.log.accessMediumNumber') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('physicalAccess.log.memberContractStatus') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('physicalAccess.log.gateWasUsed') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="identification in sortedIdentifications"
                            :key="identification.id + identification.isNew"
                            class="kgr-table__row  kgr-table__row--top"
                            :class="{ 'kgr-table__row--highlight': identification.isNew }"
                        >
                            <td class="kgr-table__cell">
                                {{ identification.date }}
                            </td>
                            <td class="kgr-table__cell">
                                {{ identification.time }}
                            </td>
                            <td class="kgr-table__cell">
                                {{ identification.terminalName ? identification.terminalName : 'GAT Writer' }}
                            </td>
                            <td class="kgr-table__cell">
                                <a @click="getShortProfileBasicForStartAction(identification)">{{ identification.accessMediumNumber }}</a>
                            </td>
                            <td class="kgr-table__cell">
                                <div v-if="identification.member && identification.member.contractState">
                                    <div class="kgr-postfix kgr-postfix--center kgr-postfix--gutters">
                                        <div
                                            class="kgr-postfix__fixed"
                                            style="margin-right: 5px"
                                        >
                                            <kgr-member-contract-state-icon :state="identification.member.contractState" />
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td class="kgr-table__cell">
                                {{ $t(`physicalAccess.log.${identification.accessGranted}`) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div
                    v-if="accessMediumIdentifications.length > limit"
                    class="kgr-alert kgr-alert--info"
                >
                    {{ $t('Weitere Ergebnisse wurden ausgeblendet. Verwende die Filter, um die Suche einzuschränken.') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Checkbox from '../../components/common/form/checkbox/Checkbox';
import DatePicker from '../../components/common/form/picker/DatePicker';
import EmptyData from '../../components/common/EmptyData';
import KgrMemberContractStateIcon from '../../components/members/MemberContractStateIcon.vue';
import KgrMemberPartBasic from '../../components/members/short-profile-parts/MemberPartBasic';
import KgrRadio from '../../components/common/form/radio/GenericRadio.vue';
import Lightbox from '../../components/common/modal/Lightbox';
import Loading from '../../components/common/Loading';
import MemberRepository from '../../js/repository/MemberRepository';
import MultiSelect from '../../components/common/form/select/MultiSelect';
import PhysicalAccessLogRepository from '../../js/repository/PhysicalAccessLogRepository';
import PhysicalAccessTerminalRepository from '../../js/repository/PhysicalAccessTerminalRepository';
import TimeRangePicker from '../../components/common/form/picker/TimeRangePicker.vue';
import moment from 'moment';

export default {
    components: {
        'kgr-radio': KgrRadio,
        'kgr-loader': Loading,
        'kgr-empty-data': EmptyData,
        'kgr-multi-select': MultiSelect,
        'kgr-time-range-picker': TimeRangePicker,
        'kgr-date-picker': DatePicker,
        'kgr-lightbox': Lightbox,
        'kgr-member-part-basic': KgrMemberPartBasic,
        'kgr-checkbox': Checkbox,
        KgrMemberContractStateIcon,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        interval: {
            type: Number,
            default: 5,
        },
    },
    data() {
        return {
            loading: true,
            limit: 30,
            accessMediumIdentifications: [],
            selectedTerminals: [],
            terminals: [],
            dateTimeRange: {},
            showSelectedActionMember: false,
            selectedAction: null,
            lookForNewEntriesInterval: null,
            liveUpdating: false,
            disableLiveUpdating: false,
            typeOptions: {
                'physicalAccess.log.type.terminals': true,
                'physicalAccess.log.type.writer': false,
            },
            selectedType: true,
        };
    },
    computed: {
        sortedIdentifications() {
            return [...this.accessMediumIdentifications].reverse();
        },
        filteredTerminals() {
            return this.terminals.filter((terminal) => {
                return !this.selectedTerminals.some((selectedTerminal) => terminal.value === selectedTerminal.value);
            });
        },
        formattedStartDate: {
            get() {
                return this.dateTimeRange.start.format('DD.MM.YYYY');
            },
            set(newDate) {
                newDate = moment(newDate, 'DD.MM.YYYY');
                if (!newDate.isValid()) {
                    return;
                }
                this.dateTimeRange.start = moment(this.dateTimeRange.start).set('date', newDate.date()).set('month', newDate.month()).set('year', newDate.year());
            },
        },
        formattedEndDate: {
            get() {
                return this.dateTimeRange.end.format('DD.MM.YYYY');
            },
            set(newDate) {
                newDate = moment(newDate, 'DD.MM.YYYY');
                if (!newDate.isValid()) {
                    return;
                }
                this.dateTimeRange.end = moment(this.dateTimeRange.end).set('date', newDate.date()).set('month', newDate.month()).set('year', newDate.year());
            },
        },
        formattedStartTime() {
            return this.dateTimeRange.start.format('HH:mm');
        },
        formattedEndTime() {
            return this.dateTimeRange.end.format('HH:mm');
        },
        preFilter() {
            const preFilter = {
                terminals: [],
                inDateTimeRange: {},
                limit: null,
                type: true,
            };
            preFilter.terminals = [];
            preFilter.inDateTimeRange = {};
            preFilter.limit = null;
            preFilter.type = true;

            preFilter.terminals = this.selectedTerminals.map((terminal) => terminal.label);
            preFilter.inDateTimeRange.start = this.dateTimeRange.start.format('YYYY-MM-DD HH:mm:ss');
            preFilter.inDateTimeRange.end = this.dateTimeRange.end.format('YYYY-MM-DD HH:mm:ss');
            preFilter.type = this.selectedType;

            return preFilter;
        },
    },
    created() {
        this.dateTimeRange = {
            start: moment().startOf('week').startOf('date'),
            end: moment().endOf('date'),
        };
        PhysicalAccessTerminalRepository
            .findByClub(this.clubId)
            .then((terminals) => terminals.forEach((terminal) => this.terminals.push({ label: terminal.name, value: terminal.id })));
        this.preLoad();
    },
    methods: {
        preLoad() {
            if (this.disableLiveUpdating) {
                this.liveUpdating = false;
            }
            this.load();
        },
        load() {
            if (!this.accessMediumIdentifications) {
                this.loading = true;
            }
            PhysicalAccessLogRepository
                .findByClub(this.clubId, this.preFilter)
                .then((startActions) => {
                    let hasNewElements = false;
                    this.accessMediumIdentifications.forEach((element) => element.isNew = false);
                    if (this.liveUpdating) {
                        const oldStartActions = [...this.accessMediumIdentifications];
                        const newStartActions = [];
                        // all new elements are at the end of the array
                        let newestStartAction = startActions.pop();
                        // loop until first not new element is found
                        while (newestStartAction) {
                            const oldElementIndex = oldStartActions.findIndex((element) => element.id === newestStartAction.id);
                            if (oldElementIndex === -1) {
                                hasNewElements = true;
                                newestStartAction.isNew = true;
                                newStartActions.unshift(newestStartAction);
                            } else {
                                break;
                            }
                            newestStartAction = startActions.pop();
                        }
                        this.accessMediumIdentifications = oldStartActions.concat(newStartActions);
                    } else {
                        this.accessMediumIdentifications = startActions;
                        this.loading = false;
                    }
                    if (hasNewElements) {
                        this.getShortProfileBasicForStartAction(this.accessMediumIdentifications[this.accessMediumIdentifications.length - 1]);
                    }
                })
                .finally(() => {
                    setTimeout(() => {
                        if (this.liveUpdating) {
                            this.preLoad();
                        }
                    }, 2000);
                });
        },
        onChangeTimeRange(timeRange) {
            this.dateTimeRange.start = moment(this.dateTimeRange.start).set('hour', timeRange.start.hours).set('minute', timeRange.start.minutes);
            this.dateTimeRange.end = moment(this.dateTimeRange.end).set('hour', timeRange.end.hours).set('minute', timeRange.end.minutes);
            this.preLoad();
        },
        liveUpdate(checked) {
            this.liveUpdating = checked;
            if (checked) {
                this.selectedType = true;
                this.preLoad();
            }
        },
        getShortProfileBasicForStartAction(action) {
            if (action) {
                if (this.selectedAction !== null && action.id !== this.selectedAction.id) {
                    this.selectedAction = null;
                }
                if (!action.shortProfile.fullname) {
                    MemberRepository
                        .getShortProfileBasic(action.member.id)
                        .then((shortProfile) => {
                            action.shortProfile = shortProfile;
                            this.showSelectedActionMember = true;
                            this.selectedAction = action;
                        });
                } else {
                    this.selectedAction = action;
                }
            }
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
</style>
